/**
 * DirectoryNameBox: a text box that is the field's own value and a
 * combobox over the Directory's entries of one kind.
 *
 * The text is what is saved. A keystroke that leaves two or more
 * characters opens the list and, after a short wait, searches the
 * Directory for the one kind the box lists; only the answer to the text
 * as it stands is shown, and a failed search shows the no-match line.
 * When results arrive the first is active; ArrowDown and ArrowUp move
 * the active row and wrap at the ends; Enter picks the active row and
 * never submits the dialog; a click picks too, and its mousedown keeps
 * the focus in the box. A box lists one kind of entry, or people and
 * organizations both. A pick fills the box with
 * the entry's name and hands the host its id and kind; while the host holds the
 * link the box says "In the Directory.". Any keystroke hands the host the
 * new text and the host drops the link, so a picked name that is then
 * edited is a typed name. With no match the list says the name is saved
 * as typed; that line is never a choice. Every answered list ends with
 * the Directory's add or request door (DirectoryAddDoor), match or not
 * (LP-26): a listing added there is picked as a result is. Escape with the list open closes the list and
 * stops there, keeping the text; with the list closed it bubbles, so the
 * dialog's own Escape closes the dialog. The list closes when the focus
 * leaves the box and the door. The box never takes the focus on mount.
 */

import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { SearchResult } from '@/lib/types';
import { DIRECTORY_SEARCH_MIN_CHARS, directoryResultLine, useDirectorySearch } from '@/lib/directorySearch';
import { textBoxProps } from '@/lib/textBoxProps';
import { TextInput } from '@/components/primitives/TextInput';
import type { FieldBoxAria } from '@/components/primitives/Field';
import { FLD } from '@/components/primitives/modalFormStyles';
import { DirectoryAddDoor } from '@/components/clientRecord/DirectoryAddDoor';

const MIN_CHARS = DIRECTORY_SEARCH_MIN_CHARS;
const LIMIT = 20;

/** The list's one line when the Directory has no match for the text. */
export const DIRECTORY_NO_MATCH = 'Saved as typed; link it to the Directory any time.';
/** The line under the box while the host holds a link. */
export const DIRECTORY_LINKED = 'In the Directory.';

export interface DirectoryNameBoxProps {
  /** The input's id (the Field's htmlFor). */
  id: string;
  /** The input's data-testid; the list and its lines derive theirs. */
  testId: string;
  /** The typed or picked name. */
  value: string;
  /** Every keystroke; the host drops the link. */
  onChange: (next: string) => void;
  /** A pick; the host sets the value to the name and keeps the id (and
   *  the entry's kind, for a box that lists both). */
  onPick: (entry: { id: string; name: string; type: 'organization' | 'individual' }) => void;
  /** The one kind of entry this box lists; both people and
   *  organizations when absent (a contact's Directory search). */
  only?: 'organization';
  /** The listbox's accessible name. */
  listLabel: string;
  /** True after a pick, until the next keystroke. */
  linked: boolean;
  /** The Field's aria attributes for the box. */
  boxAria: FieldBoxAria;
}

export function DirectoryNameBox({
  id,
  testId,
  value,
  onChange,
  onPick,
  only,
  listLabel,
  linked,
  boxAria,
}: DirectoryNameBoxProps): ReactNode {
  const listboxId = useId();
  // Open from a keystroke that leaves enough text, until a pick, Escape
  // or a blur closes it.
  const [open, setOpen] = useState(false);
  // The active row's index into the results; the first when they arrive.
  const [active, setActive] = useState(0);

  const q = value.trim();
  const expanded = open && q.length >= MIN_CHARS;
  // Only the answer to the text as it stands, so nothing from an earlier
  // query can be picked; a new query's results open on their first row.
  const { rows: results, loading } = useDirectorySearch({ q, enabled: expanded, limit: LIMIT, ...(only !== undefined ? { type: only } : {}) });
  useEffect(() => {
    setActive(0);
  }, [q]);

  const optionId = (row: SearchResult): string => `${listboxId}-${row.id}`;
  const activeIndex = results.length === 0 ? -1 : Math.min(active, results.length - 1);
  const activeRow = activeIndex === -1 ? undefined : results[activeIndex];
  const activeId = activeRow === undefined ? undefined : optionId(activeRow);

  // Keep the active row in view (where the method exists).
  useEffect(() => {
    if (activeId === undefined) return;
    const el = document.getElementById(activeId);
    if (el !== null && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function pick(row: SearchResult): void {
    setOpen(false);
    onPick({ id: row.id, name: row.display_name, type: row.type });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (results.length === 0) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : results.length - 1;
      setActive((i) => (Math.min(i, results.length - 1) + step) % results.length);
      return;
    }
    if (e.key === 'Enter') {
      if (activeRow === undefined) return;
      e.preventDefault();
      pick(activeRow);
      return;
    }
    if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  // A place name takes its name case on leaving the box, as every place
  // box does; a picked name is the Directory's and is left as it is.
  const place = textBoxProps('place');

  return (
    <div
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <TextInput
        id={id}
        data-testid={testId}
        type="text"
        className={FLD}
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          setOpen(next.trim().length >= MIN_CHARS);
          onChange(next);
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={expanded ? listboxId : undefined}
        aria-expanded={expanded}
        aria-activedescendant={expanded ? activeId : undefined}
        autoComplete="off"
        {...place}
        nameCase={linked ? undefined : place.nameCase}
        {...boxAria}
      />
      {expanded && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={listLabel}
          data-testid={`${testId}-list`}
          className="mt-1 max-h-44 overflow-y-auto rounded-base border border-sage-200 bg-paper"
        >
          {loading && <li className="px-2 py-1 text-xs text-sage-400">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="px-2 py-1 text-xs text-sage-400">{DIRECTORY_NO_MATCH}</li>
          )}
          {results.map((row, i) => (
            <li
              key={row.id}
              id={optionId(row)}
              role="option"
              aria-selected={i === activeIndex}
              data-testid={`${testId}-option`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(row)}
              className={`cursor-pointer break-words px-2 py-1 text-sm text-ink hover:bg-sage-50${
                i === activeIndex ? ' bg-sage-50' : ''
              }`}
            >
              <span className="block">{row.display_name}</span>
              {directoryResultLine(row) !== '' && (
                <span className="block text-xs text-ink-soft">{directoryResultLine(row)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <DirectoryAddDoor
        query={value}
        settled={expanded && !loading}
        onAdded={pick}
        {...(only !== undefined ? { seedType: only, only } : {})}
        testId={`${testId}-door`}
      />
      {linked && (
        <p role="status" data-testid={`${testId}-directory`} className="mt-1 text-xs text-ink-soft">
          {DIRECTORY_LINKED}
        </p>
      )}
    </div>
  );
}
